import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function TeamsConfigPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/teams-config`, { headers: getAuthHeaders() });
      setConfig(resp.data);
    } catch { toast.error("Failed to load Teams config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/teams-config`, config, { headers: getAuthHeaders() });
      toast.success("Teams configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading Teams configuration...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="teams-config-page">
        <PageHeader title="Microsoft Teams Webhook" subtitle="Send critical alerts and notifications to a Teams channel" testId="teams-config-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="teams-save-btn">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </PageHeader>

        <IntegrationHealthBadge
          integration="teams"
          onTestConnection={async () => {
            const resp = await axios.post(`${API}/admin/teams-test`, {}, { headers: getAuthHeaders() });
            return resp.data;
          }}
        />

        <div className="bg-white border border-[#E2E8F0] p-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare size={20} className="text-[#6366F1]" />
            <h2 className="font-bold text-lg text-[#0F172A]">Webhook Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium w-24">Enabled</Label>
              <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="teams-enabled-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className="text-xs text-[#64748B]">{config.enabled ? "Active" : "Disabled"}</span>
            </div>

            <div>
              <Label className="text-sm font-medium">Incoming Webhook URL *</Label>
              <Input value={config.webhook_url || ""} onChange={(e) => update("webhook_url", e.target.value)} placeholder="https://outlook.office.com/webhook/..." className="mt-1 rounded-none font-mono text-sm" data-testid="teams-webhook-url" />
              <p className="text-xs text-[#94A3B8] mt-1">Create an Incoming Webhook connector in your Teams channel settings</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4">Notification Triggers</h3>
          <div className="space-y-3">
            {[
              { key: "notify_critical_alerts", label: "Critical Alerts", desc: "Missing blueprints, flagged documents" },
              { key: "notify_overdue_maintenance", label: "Overdue Maintenance", desc: "Maintenance past scheduled date" },
              { key: "notify_scan_failures", label: "Scan Failures", desc: "Network scan errors or failures" },
              { key: "notify_work_orders", label: "Work Order Updates", desc: "New, approved, or completed work orders" },
            ].map(t => (
              <div key={t.key} className="flex items-center justify-between p-3 border border-[#E2E8F0]" data-testid={`trigger-${t.key}`}>
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">{t.label}</p>
                  <p className="text-xs text-[#64748B]">{t.desc}</p>
                </div>
                <button onClick={() => update(t.key, !config[t.key])} className={`w-10 h-5 rounded-full transition-colors ${config[t.key] ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`}>
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${config[t.key] ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
